import { AlertCircle, ArrowUpRight, CalendarClock, CreditCard, RefreshCw, ShieldCheck } from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { useSubscription } from '@/hooks/subscription';
import { manageSubscription } from '@/hooks/subscription/checkoutHelpers';
import type { SubscriptionStatus } from '@/hooks/subscription/types';
import { refreshSubscriptionSummary } from '@/services/subscriptionApi';
import { formatSourceName, formatTierName } from '@/utils/subscription/formatUtils';

const formatDate = (value?: string | null) => {
  if (!value) {
    return 'Not set';
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return 'Not set';
  }

  return date.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

const getStatusLabel = (summary: SubscriptionStatus) => {
  switch (summary.status) {
    case 'active':
      return 'Active';
    case 'grace_period':
      return 'Grace period';
    case 'requires_action':
      return 'Billing action required';
    case 'scheduled_change':
      return 'Scheduled change';
    case 'canceled':
      return summary.subscribed ? 'Scheduled cancellation' : 'Canceled';
    case 'expired':
      return 'Expired';
    case 'trialing':
      return 'Scheduled start';
    case 'past_due':
      return 'Past due';
    default:
      return 'Starter';
  }
};

const statusBadgeClassName = (summary: SubscriptionStatus) => {
  if (summary.status === 'grace_period' || summary.status === 'requires_action' || summary.status === 'past_due') {
    return 'border-amber bg-amber-soft text-brand-ink';
  }

  if (summary.subscribed) {
    return 'border-teal bg-teal-soft text-teal';
  }

  return 'border-brand-cloud bg-brand-mist text-brand-slate';
};

const isStripeManaged = (summary: SubscriptionStatus) => summary.subscription_source === 'stripe';

const getStatusDescription = (summary: SubscriptionStatus) => {
  switch (summary.status) {
    case 'grace_period':
      return `Paid access remains active until ${formatDate(summary.gracePeriodEndsAt)} while payment is resolved.`;
    case 'requires_action':
      return summary.gracePeriodEndsAt
        ? `Billing action is required. Paid access remains active until ${formatDate(summary.gracePeriodEndsAt)} before the subscription returns to Starter.`
        : 'Open the Stripe portal to complete the required billing action.';
    case 'past_due':
      return summary.gracePeriodEndsAt
        ? `Update your payment method before ${formatDate(summary.gracePeriodEndsAt)} to avoid returning to Starter.`
        : 'Update your payment method to keep paid access active.';
    case 'canceled':
      return summary.subscribed
        ? summary.expiresAt || summary.subscription_end
          ? `Paid access ends on ${formatDate(summary.expiresAt || summary.subscription_end)} and your account returns to Starter after that.`
          : 'Your subscription is set to end and return to Starter.'
        : summary.expiresAt || summary.subscription_end
          ? `Your subscription ended on ${formatDate(summary.expiresAt || summary.subscription_end)}.`
          : 'Your subscription has been canceled.';
    case 'expired':
      return summary.expiresAt || summary.subscription_end
        ? `Your paid access ended on ${formatDate(summary.expiresAt || summary.subscription_end)}.`
        : 'Your paid access has ended.';
    case 'scheduled_change':
      return summary.scheduledTierCode
        ? `${formatTierName(summary.scheduledTierCode)} starts on ${formatDate(summary.scheduledStartsAt || summary.expiresAt)}.`
        : 'A plan change is scheduled.';
    case 'trialing':
      return summary.startsAt ? `Paid access starts on ${formatDate(summary.startsAt)}.` : 'Paid access is scheduled.';
    case 'active':
      return 'Your paid benefits are active.';
    default:
      return 'Upgrade when you are ready to unlock more listing tools.';
  }
};

const getRenewalLabel = (summary: SubscriptionStatus) => {
  if (summary.cancelAtPeriodEnd || summary.status === 'canceled' || summary.status === 'expired') {
    return 'Ends';
  }

  if (summary.status === 'trialing') {
    return 'Starts';
  }

  return summary.subscribed ? 'Renews' : 'Renewal';
};

const needsBillingAttention = (summary: SubscriptionStatus) =>
  summary.status === 'requires_action' || summary.status === 'grace_period' || summary.status === 'past_due';

export default function SubscriptionTab() {
  const { data: subscriptionStatus, isLoading, isFetching, refetch } = useSubscription();
  const navigate = useNavigate();
  const [isManualRefreshing, setIsManualRefreshing] = useState(false);

  const summary = subscriptionStatus;

  const handleManage = async () => {
    await manageSubscription();
  };

  const handleRefresh = async () => {
    setIsManualRefreshing(true);

    try {
      await refreshSubscriptionSummary();
      await refetch();
      toast.success('Subscription status refreshed from Stripe.');
    } catch (error) {
      console.error('Error refreshing subscription from Stripe:', error);
      toast.error('Failed to refresh the Stripe subscription state.');
    } finally {
      setIsManualRefreshing(false);
    }
  };

  if (isLoading || !summary) {
    return (
      <div className="rounded-md border border-brand-cloud bg-white p-6 shadow-sm">
        <div className="h-6 w-40 animate-pulse rounded-md bg-brand-cloud" />
        <div className="mt-5 h-14 animate-pulse rounded-md bg-brand-cloud" />
        <div className="mt-6 space-y-3">
          <div className="h-10 animate-pulse rounded-md bg-brand-cloud" />
          <div className="h-10 animate-pulse rounded-md bg-brand-cloud" />
          <div className="h-10 animate-pulse rounded-md bg-brand-cloud" />
        </div>
      </div>
    );
  }

  const renewalDate = formatDate(summary.expiresAt || summary.subscription_end || summary.startsAt);
  const scheduledChange = summary.scheduledTierCode
    ? `${formatTierName(summary.scheduledTierCode)} on ${formatDate(summary.scheduledStartsAt || summary.expiresAt)}`
    : summary.status === 'canceled' && summary.subscribed
      ? `Starter plan on ${formatDate(summary.expiresAt || summary.subscription_end)}`
    : 'None';

  return (
    <section className="overflow-hidden rounded-md border border-brand-cloud bg-white shadow-sm">
      <div className="border-b border-brand-cloud bg-white p-6 md:p-8">
        <div className="flex flex-col gap-5 md:flex-row md:items-start md:justify-between">
          <div>
            <h2 className="public-section-title">
              {summary.planName || formatTierName(summary.tier)}
            </h2>
            <p className="mt-2 max-w-2xl text-sm leading-6 text-brand-slate">
              {getStatusDescription(summary)}
            </p>
          </div>

          <span className={`inline-flex w-fit items-center rounded-full border px-3 py-1 text-sm font-semibold ${statusBadgeClassName(summary)}`}>
            {getStatusLabel(summary)}
          </span>
        </div>

        <div className="mt-6 flex flex-wrap gap-3">
          {summary.manageable && (
            <Button onClick={handleManage} className="bg-brand-ink text-white hover:bg-brand-charcoal">
              Manage billing
            </Button>
          )}
          <Button
            variant={summary.manageable ? 'outline' : 'default'}
            onClick={() => navigate('/pricing')}
            className={!summary.manageable ? 'bg-brand-ink text-white hover:bg-brand-charcoal' : undefined}
          >
            View plans
            <ArrowUpRight className="ml-2 h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            onClick={() => void handleRefresh()}
            disabled={isManualRefreshing}
            className="text-brand-slate hover:text-brand-ink"
          >
            <RefreshCw className={`mr-2 h-4 w-4 ${isManualRefreshing ? 'animate-spin' : ''}`} />
            {isManualRefreshing ? 'Refreshing…' : 'Refresh'}
          </Button>
        </div>
      </div>

      {needsBillingAttention(summary) && (
        <div className="border-b border-amber bg-amber-soft px-6 py-4 md:px-8">
          <div className="flex gap-3">
            <AlertCircle className="mt-0.5 h-5 w-5 shrink-0 text-amber-700" />
            <div>
              <p className="font-medium text-brand-ink">Billing attention needed</p>
              <p className="mt-1 text-sm leading-6 text-brand-slate">
                {getStatusDescription(summary)}
              </p>
            </div>
          </div>
        </div>
      )}

      <dl className="divide-y divide-brand-cloud">
        <div className="grid gap-2 px-6 py-5 md:grid-cols-[220px_1fr] md:items-center md:px-8">
          <dt className="flex items-center gap-2 text-sm font-medium text-brand-slate">
            <CalendarClock className="h-4 w-4" />
            {getRenewalLabel(summary)}
          </dt>
          <dd className="text-sm font-semibold text-brand-ink">{renewalDate}</dd>
        </div>

        <div className="grid gap-2 px-6 py-5 md:grid-cols-[220px_1fr] md:items-center md:px-8">
          <dt className="flex items-center gap-2 text-sm font-medium text-brand-slate">
            <CreditCard className="h-4 w-4" />
            Next charge
          </dt>
          <dd className="text-sm font-semibold text-brand-ink">
            {summary.status === 'canceled' || summary.status === 'expired'
              ? 'Not scheduled'
              : summary.nextChargeAmount?.formatted || 'Not scheduled'}
          </dd>
        </div>

        <div className="grid gap-2 px-6 py-5 md:grid-cols-[220px_1fr] md:items-center md:px-8">
          <dt className="flex items-center gap-2 text-sm font-medium text-brand-slate">
            <ShieldCheck className="h-4 w-4" />
            Managed by
          </dt>
          <dd className="text-sm font-semibold text-brand-ink">
            {summary.subscription_source ? formatSourceName(summary.subscription_source) : 'Starter plan'}
          </dd>
        </div>

        <div className="grid gap-2 px-6 py-5 md:grid-cols-[220px_1fr] md:items-center md:px-8">
          <dt className="text-sm font-medium text-brand-slate">Scheduled change</dt>
          <dd className="text-sm font-semibold text-brand-ink">{scheduledChange}</dd>
        </div>
      </dl>

      {isStripeManaged(summary) && summary.subscribed && (
        <div className="border-t border-brand-cloud bg-brand-mist px-6 py-4 text-sm leading-6 text-brand-slate md:px-8">
          Use the Stripe portal for upgrades, downgrades, cancellation, and payment method changes.
        </div>
      )}
    </section>
  );
}
